import { Check, CheckCircle2, Clock, Search, Users } from "lucide-react";
import { useProjectManagementContext } from "../context";
import EvidenceVideoPlayer from "./EvidenceVideoPlayer";
import { isEvidenceVideo, resolveEvidenceMediaUrl } from "../../../lib/imageUrl";

const evidenceImageUrl = (
  ev?: { url?: string | null; image?: string | null } | string | null,
) => resolveEvidenceMediaUrl(ev);

const ProjectManagementModals = () => {
  const {
    allProjects,
    handleLoadProject,
    handleNewProject,
    handleDeleteProject,
    isHistoryModalOpen,
    isInventoryModalOpen,
    isRejectModalOpen,
    isTeamModalOpen,
    isEvidenceModalOpen,
    isVendorModalOpen,
    setBaselineData,
    baselineData,
    setIsEvidenceModalOpen,
    setIsHistoryModalOpen,
    setIsInventoryModalOpen,
    setIsRejectModalOpen,
    setIsTeamModalOpen,
    setIsVendorModalOpen,
    setNewItem,
    setNewVendor,
    setRejectReason,
    activeProjectId,
    activeEvidence,
    activeTaskId,
    allTechnicians,
    architectVendors,
    isLoadingVendors,
    materialBudget,
    currentVendorCosts,
    newVendor,
    newItem,
    rejectReason,
    techSearch,
    setTechSearch,
    taskTeams,
    handleAssignTech,
    handleRejectValidation,
    activeValidationId,
  } = useProjectManagementContext();

  const totalBudget = baselineData?.budget || 0;
  const projectsList = [...allProjects].reverse();

  return (
    <>
      {/* =========================================================================
      1. MODAL RIWAYAT PROYEK
     ========================================================================= */}
      {isHistoryModalOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/20 backdrop-blur-sm animate-[fadeIn_0.3s_ease]"
          onClick={() => setIsHistoryModalOpen(false)}
        >
          <div
            className="flex h-full max-h-[80vh] w-full max-w-[96vw] sm:w-[640px] flex-col overflow-hidden rounded-[20px] border border-solid border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-solid border-slate-200 px-7 py-6">
              <div>
                <h3 className="m-0 text-[1.1rem] font-extrabold text-slate-800">
                  Riwayat Proyek
                </h3>
                <p className="m-0 mt-0.5 text-[0.82rem] text-slate-400">
                  {projectsList.length} proyek tersimpan di perangkat ini
                </p>
              </div>
              <button
                onClick={() => setIsHistoryModalOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full border-none bg-slate-100 text-[1.1rem] text-slate-500 cursor-pointer"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-7 py-4">
              {projectsList.length === 0 ? (
                <div className="py-12 text-center text-slate-400">
                  <p className="text-sm">belum ada project disimpan</p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {projectsList.map((proj: any) => {
                    const approvedCount = proj.completed_tasks_count || 0;
                    const totalTasks = proj.tasks_count || 0;
                    const progress =
                      totalTasks > 0
                        ? Math.round((approvedCount / totalTasks) * 100)
                        : 0;
                    const isActive = activeProjectId === proj.id;

                    return (
                      <div
                        key={proj.id}
                        className={`flex justify-between gap-3.5 p-4 rounded-[14px] ${isActive
                            ? "border-[1.5px] border-solid border-blue-500 bg-blue-50"
                            : "border border-solid border-slate-200 bg-white"
                          }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2.5">
                            <div>
                              <div className="font-bold text-[0.95rem] text-slate-800">
                                {proj.name}
                              </div>
                              <div className="mt-1 text-[0.78rem] text-slate-400">
                                {proj.deadline_date || "Tanpa deadline"}
                              </div>
                            </div>
                            {isActive && (
                              <span className="inline-block shrink-0 rounded-lg bg-blue-100 px-2 py-1 text-[0.68rem] font-bold text-blue-600">
                                AKTIF
                              </span>
                            )}
                          </div>

                          {/* Progress Bar */}
                          <div className="mt-3">
                            <div className="flex justify-between text-[0.76rem] text-slate-500 mb-1.5">
                              <span>Progress</span>
                              <span>{progress}%</span>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                              <div
                                className={`h-full rounded-[2px] transition-all duration-300 ${progress >= 80
                                    ? "bg-emerald-500"
                                    : progress >= 50
                                      ? "bg-amber-500"
                                      : "bg-blue-500"
                                  }`}
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-shrink-0 flex-col gap-1.5">
                          {!isActive && (
                            <button
                              onClick={() => handleLoadProject(proj)}
                              className="rounded-lg border-none bg-blue-600 px-3.5 py-1.5 text-xs font-semibold text-white cursor-pointer"
                            >
                              Buka
                            </button>
                          )}
                          {!isActive && (
                            <button
                              onClick={async () => {
                                await handleDeleteProject(proj);
                              }}
                              className="rounded-lg border border-solid border-red-200 bg-red-50 px-3.5 py-1.5 text-xs font-semibold text-red-500 cursor-pointer"
                            >
                              Hapus
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-solid border-slate-200 px-7 py-4">
              <button
                onClick={handleNewProject}
                className="flex items-center gap-1.5 rounded-xl border-[1.5px] border-solid border-emerald-300 bg-emerald-50 px-4.5 py-2.5 text-[0.88rem] font-bold text-emerald-500 cursor-pointer"
              >
                + Proyek Baru
              </button>
              <button
                onClick={() => setIsHistoryModalOpen(false)}
                className="rounded-xl border-none bg-slate-100 px-4.5 py-2.5 text-[0.88rem] font-semibold text-slate-500 cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
      2. MODAL TAMBAH VENDOR
     ========================================================================= */}
      {isVendorModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/20 backdrop-blur-sm animate-[fadeIn_0.3s_ease]">
          <div className="w-full max-w-[500px] rounded-3xl border border-solid border-slate-200 bg-white p-[30px] shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-solid border-slate-200 pb-6 mb-6">
              <h3 className="m-0 text-lg font-bold text-slate-800">Tambah Vendor & Biaya</h3>
              <button
                className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-[1.2rem] text-slate-500 transition-all duration-200 hover:text-slate-800 cursor-pointer"
                onClick={() => setIsVendorModalOpen(false)}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="max-h-[70vh] overflow-y-auto project-modal-scroll">
              {/* Budget Info */}
              <div className="mb-6 rounded border border-solid border-slate-200 bg-slate-50 p-3">
                <div className="flex justify-between mb-1">
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    Total Anggaran Proyek:
                  </span>
                  <strong className="font-semibold text-slate-800">
                    {new Intl.NumberFormat("id-ID", {
                      style: "currency",
                      currency: "IDR",
                      maximumFractionDigits: 0,
                    }).format(totalBudget)}
                  </strong>
                </div>
                <div className="flex justify-between">
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    Sisa Anggaran Tersedia:
                  </span>
                  <strong
                    className={
                      totalBudget - currentVendorCosts < 0
                        ? "text-red-500"
                        : "text-emerald-500"
                    }
                  >
                    {new Intl.NumberFormat("id-ID", {
                      style: "currency",
                      currency: "IDR",
                      maximumFractionDigits: 0,
                    }).format(totalBudget - currentVendorCosts)}
                  </strong>
                </div>
              </div>

              <div className="space-y-4">
                {/* Select Vendor */}
                {/* <div className="flex flex-col">
                  <label className="font-bold text-slate-800">Pilih Partner Architect (Vendor)</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-solid border-slate-300 p-2.5 text-[0.85rem] text-slate-800"
                    value={newVendor.vendor_id || ""}
                    onChange={(e) => {
                      const selected = architectVendors.find(
                        (v: any) => v.id === e.target.value,
                      );
                      if (selected) {
                        setNewVendor({
                          ...newVendor,
                          vendor_id: selected.id,
                          name: `${selected.first_name} ${selected.last_name || ""}`,
                        });
                      }
                    }}
                  >
                    <option value="">-- Pilih Vendor --</option>
                    {architectVendors.map((v: any) => (
                      <option key={v.id} value={v.id}>
                        {v.first_name} {v.last_name} ({v.phone})
                      </option>
                    ))}
                  </select>

                  {isLoadingVendors && (
                    <small className="mt-1 text-xs text-slate-400">
                      Memuat daftar vendor...
                    </small>
                  )}
                </div> */}

                {/* Manual Name */}
                <div className="flex flex-col">
                  <label className="font-bold text-slate-800">
                    Nama Vendor / PT
                  </label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded-lg border border-solid border-slate-300 p-2.5 text-[0.85rem] text-slate-800"
                    placeholder="Nama PT / Toko"
                    value={newVendor.name}
                    onChange={(e) =>
                      setNewVendor({ ...newVendor, name: e.target.value })
                    }
                  />
                </div>

                {/* Task */}
                <div className="flex flex-col">
                  <label className="font-bold text-slate-800">Tugas / Item Procurement</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded-lg border border-solid border-slate-300 p-2.5 text-[0.85rem] text-slate-800"
                    placeholder="Misal: Supply Chemical / Sewa Alat"
                    value={newVendor.task}
                    onChange={(e) =>
                      setNewVendor({ ...newVendor, task: e.target.value })
                    }
                  />
                </div>

                {/* Cost */}
                <div className="flex flex-col mb-6">
                  <label className="font-bold text-slate-800">Nilai Kontrak (Biaya)</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded-lg border border-solid border-slate-300 p-2.5 text-[0.85rem] text-slate-800"
                    placeholder="Rp 0"
                    value={new Intl.NumberFormat("id-ID").format(newVendor.cost ?? 0)}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setNewVendor({ ...newVendor, cost: parseInt(val) || 0 });
                    }}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-2 mt-6">
                <button
                  className="flex-1 rounded-xl border-0 bg-slate-100 px-5 py-3 font-bold text-slate-500 cursor-pointer"
                  onClick={() => setIsVendorModalOpen(false)}
                >
                  Batal
                </button>
                <button
                  className="flex-1 items-center justify-center rounded-lg border-0 bg-blue-600 px-5 py-3 font-bold text-white transition-all duration-200 hover:bg-blue-700 cursor-pointer"
                  onClick={() => {
                    setBaselineData((prev: any) => ({
                      ...prev,
                      vendors: [
                        ...prev.vendors,
                        { ...newVendor, id: Date.now(), status: "Pending" },
                      ],
                    }));
                    setNewVendor({
                      name: "",
                      task: "",
                      cost: 0,
                      vendor_id: null,
                    });
                    setIsVendorModalOpen(false);
                  }}
                >
                  Simpan Vendor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
      3. MODAL PENDAFTARAN ALAT
     ========================================================================= */}
      {isInventoryModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/20 backdrop-blur-sm animate-[fadeIn_0.3s_ease]">
          <div className="w-full max-w-[500px] rounded-3xl border border-solid border-slate-200 bg-white p-[30px] shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center justify-between border-b border-solid border-slate-200 pb-6 mb-6">
              <h3 className="m-0 text-lg font-bold text-slate-800">Pendaftaran Alat Proyek</h3>
              <button
                className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-[1.2rem] text-slate-500 transition-all duration-200 hover:text-slate-800 cursor-pointer"
                onClick={() => setIsInventoryModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              <div className="mb-6 flex flex-col">
                <label className="font-bold text-slate-800">Nama Alat / Inventaris</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-lg border border-solid border-slate-300 p-2.5 text-[0.85rem] text-slate-800"
                  placeholder="Misal: Vacuum Cleaner Industrial"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ name: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <button
                  className="flex-1 rounded-xl border-0 bg-slate-100 px-5 py-3 font-bold text-slate-500 cursor-pointer"
                  onClick={() => setIsInventoryModalOpen(false)}
                >
                  Batal
                </button>
                <button
                  className="flex-1 items-center justify-center rounded-lg border-0 bg-blue-600 px-5 py-3 font-bold text-white transition-all duration-200 hover:bg-blue-700 cursor-pointer"
                  onClick={() => {
                    setBaselineData((prev: any) => ({
                      ...prev,
                      inventory: [
                        ...prev.inventory,
                        {
                          id: Date.now(),
                          name: newItem.name,
                          status: "Pending",
                        },
                      ],
                    }));
                    setNewItem({ name: "" });
                    setIsInventoryModalOpen(false);
                  }}
                >
                  Simpan Alat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
      4. MODAL ASSIGN TEAM
     ========================================================================= */}
      {isTeamModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/20 backdrop-blur-sm animate-[fadeIn_0.3s_ease]">
          <div className="w-full max-w-[600px] rounded-3xl border border-solid border-slate-200 bg-white p-[30px] shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center justify-between border-b border-solid border-slate-200 pb-6 mb-6">
              <div>
                <h3 className="m-0 text-lg font-bold text-slate-800">Assign Team</h3>
                <small className="text-xs text-slate-400">
                  Pilih tukang/serviceman yang akan menerima task di mobile app
                </small>
              </div>
              <button
                className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-[1.2rem] text-slate-500 transition-all duration-200 hover:text-slate-800 cursor-pointer"
                onClick={() => setIsTeamModalOpen(false)}
              >
                ×
              </button>
            </div>

            {/* Search Input */}
            <div className="mb-6">
              <div className="flex items-center gap-2 rounded-lg border border-solid border-slate-200 p-2 transition-all duration-200 focus-within:border-blue-600 focus-within:shadow-[0_0_0_4px_rgba(37,99,235,0.1)]">
                <Search size={18} className="text-slate-400" />
                <input
                  type="text"
                  className="w-full border-0 bg-transparent outline-none text-slate-800"
                  placeholder="Cari tukang / serviceman..."
                  value={techSearch}
                  onChange={(e) => setTechSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Technicians List */}
            <div className="max-h-[360px] overflow-y-auto pr-1">
              {allTechnicians
                .filter((tech: any) =>
                  `${tech.first_name || ""} ${tech.last_name || ""}`
                    .toLowerCase()
                    .includes(techSearch.toLowerCase()),
                )
                .map((tech: any) => {
                  const isAssigned = (
                    taskTeams[String(activeTaskId)] || []
                  ).some((at: any) => at.id === tech.id);

                  return (
                    <div
                      key={tech.id}
                      className={`flex items-center gap-3 p-3 rounded-xl mb-2 cursor-pointer transition-colors ${isAssigned ? "bg-blue-50 border border-solid border-blue-200" : "hover:bg-slate-50"
                        }`}
                      onClick={() =>
                        activeTaskId !== null && handleAssignTech(activeTaskId, tech)
                      }
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-[1.1rem] font-extrabold text-slate-500 transition-all duration-200">
                        {tech.first_name?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1">
                        <strong className="text-slate-800">
                          {tech.first_name} {tech.last_name || ""}
                        </strong>
                        <div className="text-xs text-slate-400">
                          {tech.phone || "-"}
                        </div>
                      </div>
                      {isAssigned && (
                        <div className="text-blue-600">
                          <CheckCircle2 size={18} />
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            <div className="flex justify-end mt-6">
              <button
                className="rounded-lg bg-slate-100 px-4 py-2 font-semibold text-slate-500 cursor-pointer border-0"
                onClick={() => setIsTeamModalOpen(false)}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
      5. MODAL ALASAN PENOLAKAN
     ========================================================================= */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/20 backdrop-blur-sm animate-[fadeIn_0.3s_ease]">
          <div className="w-full max-w-[500px] rounded-3xl border border-solid border-slate-200 bg-white p-[30px] shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center justify-between border-b border-solid border-slate-200 pb-6 mb-6">
              <h3 className="m-0 text-lg font-bold text-slate-800">Alasan Penolakan</h3>
              <button
                className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-[1.2rem] text-slate-500 transition-all duration-200 hover:text-slate-800 cursor-pointer"
                onClick={() => setIsRejectModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto">
              <p className="mb-6 text-slate-600 text-sm">
                Pekerjaan ini akan dikirim kembali ke mobile app tukang/serviceman sebagai{" "}
                <strong className="font-semibold text-slate-800">perbaikan</strong>
                . Jelaskan dengan singkat apa yang perlu diperbaiki.
              </p>

              <textarea
                className="w-full rounded-lg border border-solid border-slate-300 p-2.5 text-[0.85rem] text-slate-800"
                rows={4}
                placeholder="Misal: Lantai masih licin dan ada sisa debu di sudut ruangan..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />

              <div className="flex gap-2 mt-6">
                <button
                  className="flex-1 rounded-xl border-0 bg-slate-100 px-5 py-3 font-bold text-slate-500 cursor-pointer"
                  onClick={() => setIsRejectModalOpen(false)}
                >
                  Batal
                </button>
                <button
                  className="flex-1 items-center justify-center rounded-lg border-0 bg-red-500 px-5 py-3 font-bold text-white transition-all duration-200 hover:bg-red-600 cursor-pointer"
                  onClick={() =>
                    activeValidationId !== null &&
                    handleRejectValidation(activeValidationId, rejectReason)
                  }
                >
                  Tolak Pekerjaan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
      6. MODAL GALERI EVIDENCE
     ========================================================================= */}
      {isEvidenceModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/20 backdrop-blur-sm animate-[fadeIn_0.3s_ease]">
          <div className="w-full max-w-[760px] rounded-3xl border border-solid border-slate-200 bg-white p-[30px] shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center justify-between border-b border-solid border-slate-200 pb-6 mb-6">
              <h3 className="m-0 text-lg font-bold text-slate-800">Galeri Evidence</h3>
              <button
                className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-[1.2rem] text-slate-500 transition-all duration-200 hover:text-slate-800 cursor-pointer"
                onClick={() => setIsEvidenceModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto">
              {activeEvidence?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4 max-h-[500px] overflow-y-auto p-1 text-slate-500">
                  {activeEvidence.map((ev: any, idx: number) => {
                    const isVideo = isEvidenceVideo(ev) || ev?.type === "video";
                    const url = evidenceImageUrl(ev);

                    return (
                      <div
                        key={idx}
                        className="overflow-hidden rounded-xl border border-solid border-slate-200 shadow-sm"
                      >
                        {isVideo ? (
                          <EvidenceVideoPlayer url={url} className="rounded-none" />
                        ) : (
                          <img
                            src={url}
                            alt={`Evidence ${idx + 1}`}
                            className="h-40 w-full object-cover"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src =
                                "https://indoteknisi.net/public/assets/admin-module/img/media/service.png";
                            }}
                          />
                        )}
                        <div className="border-t border-solid border-slate-200 bg-slate-50 p-2">
                          <small className="font-bold text-slate-700">
                            {isVideo ? "Video" : "Foto"} #{idx + 1}
                          </small>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-12 text-center text-slate-400">
                  <Users size={32} className="mx-auto mb-3 opacity-30" />
                  <p>Belum ada evidence tersimpan</p>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  className="rounded-lg bg-slate-100 px-4 py-2 font-semibold text-slate-500 cursor-pointer border-0"
                  onClick={() => setIsEvidenceModalOpen(false)}
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectManagementModals;

